import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Login from "../components/Login/Login";
import LandingFooter from "../components/LandingFooter";
import {
  clearPendingCheckout,
  readPendingCheckout,
  savePendingCheckout,
  startStripeCheckout,
} from "../utils/stripe";
import { VerificationIcon } from "../assets/icons/icons";
import useFocusTrap from "../hooks/useFocusTrap";
import "./PublicSite.css";

const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";

export default function LoginPage({ session = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage = location.pathname.startsWith("/pricing");
  const isAboutPage = location.pathname.startsWith("/about");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");
  const defaultSignUp = params.get("mode") === "signup";
  const queryPlan = params.get("plan");
  const queryInterval = params.get("interval") || "monthly";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const [pendingIntent, setPendingIntent] = useState(() => readPendingCheckout());
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const checkoutStartedRef = useRef(false);
  const landingRootRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  useFocusTrap({
    isOpen: mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  const checkoutIntent = useMemo(() => {
    if (queryPlan) {
      return { plan: queryPlan, interval: queryInterval, source: "pricing" };
    }
    return pendingIntent;
  }, [queryPlan, queryInterval, pendingIntent]);

  useEffect(() => {
    if (!queryPlan) return;
    clearPendingCheckout();
    setPendingIntent(null);
  }, [queryPlan]);

  const beginCheckout = useCallback(async (intent) => {
    if (!intent || checkoutStartedRef.current) return;
    checkoutStartedRef.current = true;
    setCheckoutError("");
    setCheckoutBusy(true);
    try {
      if (!queryPlan && pendingIntent) {
        clearPendingCheckout();
        setPendingIntent(null);
      }
      await startStripeCheckout({
        ...intent,
        source: intent.source || "login",
        successPath: "/dashboard?checkout=success",
        cancelPath: "/pricing",
      });
    } catch (err) {
      checkoutStartedRef.current = false;
      setCheckoutBusy(false);
      setCheckoutError(err.message || "Unable to start checkout.");
    }
  }, [pendingIntent, queryPlan]);

  useEffect(() => {
    if (!session) return;
    if (checkoutIntent) {
      beginCheckout(checkoutIntent);
      return;
    }
    navigate("/dashboard", { replace: true });
  }, [session, checkoutIntent, navigate, beginCheckout]);

  const handleAuthSuccess = async () => {
    if (checkoutIntent) {
      await beginCheckout(checkoutIntent);
      return;
    }
    navigate("/dashboard");
  };

  const handleAuthNoSession = () => {
    if (!checkoutIntent) return;
    savePendingCheckout(checkoutIntent);
    setPendingIntent(checkoutIntent);
  };

  if (session && !checkoutIntent) {
    return null;
  }

  return (
    <div ref={landingRootRef} className="public-shell landing login-page clearbadge-public">
      <header className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}>
        <div className="landing-topbar-inner">
          <div className="landing-topbar-left">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home">
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <nav className="public-search-nav" aria-label="Search navigation">
              <Link
                className={`top-link ${location.pathname === "/" ? "active" : ""}`}
                to="/"
                data-reveal
                style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Verify a License
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/find-a-pro") ? "active" : ""}`}
                to="/find-a-pro"
                data-reveal
                style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Find a Pro
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/pricing") ? "active" : ""}`}
                to="/pricing"
                data-reveal
                style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Pricing
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/about") ? "active" : ""}`}
                to="/about"
                data-reveal
                style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                About
              </Link>
            </nav>
          </div>
          <div className="public-links landing-topbar-right">
            <Link
              className="top-link"
              to="/login"
              data-reveal
              style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Sign In
            </Link>
            <Link
              className="primary nav-cta mobile-signup"
              to="/login?mode=signup"
              data-reveal
              style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Get Verified
            </Link>
            <button
              ref={mobileMenuButtonRef}
              className="icon-btn mobile-menu-btn"
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="login-mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span className="burger-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
        <button
          type="button"
          className="public-mobile-overlay-dismiss"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        />
        <div
          id="login-mobile-menu"
          ref={mobileDrawerRef}
          className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="public-mobile-header">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home" onClick={() => setMobileMenuOpen(false)}>
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
              <span className="mobile-drawer-close-icon" aria-hidden="true" />
            </button>
          </div>
          <div className="public-mobile-links">
            <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
              Sign In
            </Link>
            <Link
              to="/login?mode=signup"
              className="public-mobile-link primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Verified
            </Link>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">TOOLS</span>
              <Link
                to="/"
                className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Verify a License</span>
              </Link>
              <Link
                to="/find-a-pro"
                className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Find a Pro</span>
              </Link>
            </div>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">RESOURCES</span>
              <Link
                to="/about"
                className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">About</span>
              </Link>
              <Link
                to="/pricing"
                className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Pricing</span>
              </Link>
              <Link
                to="/privacy-policy"
                className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Privacy Policy</span>
              </Link>
              <Link
                to="/terms-of-service"
                className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Terms of Service</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main id="main-content" className="landing-main login-main">
        <section className="hero login-hero">
          <div className="hero-inner login-hero-inner">
            <div
              className="trust-pill"
              data-reveal
              style={{ "--reveal-delay": "1400ms", "--reveal-x": "0px", "--reveal-y": "14px" }}
            >
              {defaultSignUp ? (
                "START FOR FREE"
              ) : (
                <>
                  <VerificationIcon className="pill-icon" />
                  SECURE ACCESS
                </>
              )}
            </div>
            <h1 data-reveal style={{ "--reveal-delay": "1540ms", "--reveal-x": "0px", "--reveal-y": "14px" }}>
              {defaultSignUp ? "Get Verified for Free" : "Licensee Login"}
            </h1>
            <p
              className="hero-lead"
              data-reveal
              style={{ "--reveal-delay": "1680ms", "--reveal-x": "0px", "--reveal-y": "14px" }}
            >
              {defaultSignUp ? "Verify your first license in minutes." : "Access your verification dashboard."}
            </p>
            {checkoutBusy && <p className="muted">Redirecting to Stripe Checkout...</p>}
            {checkoutError && <p className="error-message">{checkoutError}</p>}
            <div className="search-card" data-reveal style={{ "--reveal-delay": "1820ms" }}>
              <Login
                defaultSignUp={defaultSignUp}
                onAuthSuccess={handleAuthSuccess}
                onAuthNoSession={handleAuthNoSession}
              />
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}

